import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, Star, Quote } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Placeholder testimonials (shown when no real reviews exist)
const placeholderTestimonials = [
  {
    id: 'p1',
    content: "The attention to detail was exceptional. Our kitchen looks like it belongs in a magazine. The team was professional, clean, and communicated every step of the way.",
    reviewer_name: "Sarah M.",
    project_type: "Kitchen Remodel",
    rating: 5,
    isPlaceholder: true,
  },
  {
    id: 'p2',
    content: "Professional from start to finish. They kept us informed every step of the way and the quality of work exceeded our expectations. Highly recommend!",
    reviewer_name: "Michael R.",
    project_type: "Bathroom Renovation",
    rating: 5,
    isPlaceholder: true,
  },
  {
    id: 'p3',
    content: "Clean, efficient, and the results exceeded our expectations. The team respected our home and the worksite was always tidy. Couldn't be happier!",
    reviewer_name: "Jennifer L.",
    project_type: "Interior Renovation",
    rating: 5,
    isPlaceholder: true,
  },
  {
    id: 'p4',
    content: "We've worked with several contractors over the years, and Jax Remodeling is by far the best. Their communication and craftsmanship are unmatched.",
    reviewer_name: "David & Karen T.",
    project_type: "Full Home Flooring",
    rating: 5,
    isPlaceholder: true,
  },
  {
    id: 'p5',
    content: "From the initial consultation to the final walkthrough, everything was handled with professionalism. Our master bath is now our favorite room.",
    reviewer_name: "Amanda H.",
    project_type: "Master Bath Remodel",
    rating: 5,
    isPlaceholder: true,
  },
  {
    id: 'p6',
    content: "The team transformed our outdated kitchen into a modern, functional space. They listened to our needs and delivered beyond what we imagined.",
    reviewer_name: "Robert M.",
    project_type: "Kitchen Update",
    rating: 5,
    isPlaceholder: true,
  },
];

export const Reviews = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [stats, setStats] = useState({ average_rating: 5.0, total_reviews: 0, five_star_count: 0 });
  const [loading, setLoading] = useState(true);
  const [showingPlaceholders, setShowingPlaceholders] = useState(false);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reviews/public/testimonials`);
      const data = response.data;
      
      if (data.reviews && data.reviews.length > 0) {
        setTestimonials(data.reviews);
        setStats(data.stats);
        setShowingPlaceholders(false);
      } else {
        // Show placeholders if no real reviews
        setTestimonials(placeholderTestimonials);
        setShowingPlaceholders(true);
      }
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      // Fall back to placeholders
      setTestimonials(placeholderTestimonials);
      setShowingPlaceholders(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="reviews-page">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="w-16 h-px bg-[#C6A87C] mb-8" />
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-6">Client Reviews</h1>
            <p className="text-gray-600 text-lg leading-relaxed">
              Don't just take our word for it. Hear what our clients have to say about their experience with Jax Remodeling Solutions.
            </p>
            {showingPlaceholders && (
              <p className="text-gray-500 text-sm mt-4 italic">
                (Placeholder testimonials for demonstration purposes)
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="flex justify-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star 
                    key={star} 
                    className={`w-6 h-6 ${
                      star <= Math.round(stats.average_rating) 
                        ? 'fill-[#C6A87C] text-[#C6A87C]' 
                        : 'text-gray-600'
                    }`} 
                  />
                ))}
              </div>
              <p className="text-white text-4xl font-heading">{stats.average_rating}</p>
              <p className="text-gray-400 text-sm uppercase tracking-wider mt-2">Average Rating</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
            >
              <p className="text-[#C6A87C] text-5xl font-heading">{showingPlaceholders ? '50+' : stats.total_reviews || '0'}</p>
              <p className="text-gray-400 text-sm uppercase tracking-wider mt-2">Happy Clients</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              viewport={{ once: true }}
            >
              <p className="text-white text-5xl font-heading">{showingPlaceholders ? '98%' : `${stats.five_star_count}`}</p>
              <p className="text-gray-400 text-sm uppercase tracking-wider mt-2">
                {showingPlaceholders ? 'Would Recommend' : '5-Star Reviews'}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#C6A87C] text-xs tracking-[0.3em] uppercase mb-4">Testimonials</p>
            <h2 className="font-heading text-3xl md:text-4xl">What Our Clients Say</h2>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-[#F9F9F7] p-8 relative group hover:shadow-lg transition-shadow"
                >
                  <Quote className="w-10 h-10 text-[#C6A87C]/20 absolute top-6 right-6" />
                  
                  {/* Rating */}
                  <div className="flex gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-4 h-4 ${
                          star <= testimonial.rating ? 'fill-[#C6A87C] text-[#C6A87C]' : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Title if exists */}
                  {testimonial.title && (
                    <p className="font-semibold text-gray-900 mb-2">"{testimonial.title}"</p>
                  )}

                  {/* Quote */}
                  <p className="text-gray-600 leading-relaxed mb-6 relative z-10">
                    "{testimonial.content}"
                  </p>

                  {/* Author */}
                  <div className="border-t border-gray-200 pt-4">
                    <p className="font-semibold text-gray-900">{testimonial.reviewer_name}</p>
                    <p className="text-sm text-gray-500">{testimonial.project_type}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-[#C6A87C] text-xs tracking-[0.3em] uppercase mb-4">Ready to Start?</p>
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
              Join Our Satisfied Clients
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto mb-8">
              Experience the Jax Remodeling difference. Schedule your consultation today and let us transform your vision into reality.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-[#C6A87C] text-white px-8 py-4 text-xs tracking-widest uppercase hover:bg-[#B09265] transition-colors"
              >
                Schedule Consultation
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:+19046016535"
                className="inline-flex items-center justify-center gap-2 border border-white/20 text-white px-8 py-4 text-xs tracking-widest uppercase hover:bg-white/5 transition-colors"
              >
                <Phone className="w-4 h-4" />
                (904) 601-6535
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};
